import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  alpha,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { GlassCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import { useParams } from 'react-router-dom';
import {
  isHandleAvailable,
  isSubHandleAvailable,
  getUserAddresses,
} from '../../utils/handleManager';
import Coins from '../../assets/coins/coins.json';
import * as firebase from 'firebase/app';
import 'firebase/database';
import { hasEthAddress, handleToEnsStyle } from '../../utils/ensFormatter';

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
  AVAX: gradients.primary,
  DOT: gradients.lavender,
  LINK: gradients.ocean,
};

export const ProfilePage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const [loading, setLoading] = useState(true);
  const [handleExists, setHandleExists] = useState(false);
  const [addresses, setAddresses] = useState<{ [key: string]: string }>({});
  const [bio, setBio] = useState<string>('Accepting crypto payments');
  const [avatar, setAvatar] = useState<string | null>(null);

  const coins = Coins as { [key: string]: any };

  useEffect(() => {
    async function checkHandleAndLoadData() {
      if (!handle) {
        setLoading(false);
        return;
      }

      try {
        const isAvailable = await isHandleAvailable(handle);
        const isSubAvailable = await isSubHandleAvailable(handle);

        let exists = false;
        if (isAvailable) {
          if (!isSubAvailable) {
            exists = true;
          }
        } else {
          exists = true;
        }

        setHandleExists(exists);

        if (exists) {
          const userAddresses = await getUserAddresses(handle);
          setAddresses(userAddresses);

          try {
            const handleRef = firebase.database().ref(`/handles/${handle}`);
            const handleSnap = await handleRef.once('value');
            if (handleSnap.exists()) {
              const uid = handleSnap.val();
              if (uid) {
                const bioRef = firebase.database().ref(`/profiles/bio/${uid}`);
                const bioSnapshot = await bioRef.once('value');
                if (bioSnapshot.exists()) {
                  const bioValue = bioSnapshot.val();
                  if (bioValue) setBio(bioValue);
                }

                const avatarRef = firebase.database().ref(`/profiles/avatar/${uid}`);
                const avatarSnapshot = await avatarRef.once('value');
                if (avatarSnapshot.exists()) {
                  const avatarValue = avatarSnapshot.val();
                  if (avatarValue && avatarValue !== 'none' && avatarValue !== '') {
                    setAvatar(avatarValue);
                  }
                }
              }
            } else {
              const bioRef = firebase.database().ref(`/profiles/bio/${handle}`);
              const bioSnapshot = await bioRef.once('value');
              if (bioSnapshot.exists()) {
                const bioValue = bioSnapshot.val();
                if (bioValue) setBio(bioValue);
              }

              const avatarRef = firebase.database().ref(`/profiles/avatar/${handle}`);
              const avatarSnapshot = await avatarRef.once('value');
              if (avatarSnapshot.exists()) {
                const avatarValue = avatarSnapshot.val();
                if (avatarValue && avatarValue !== 'none' && avatarValue !== '') {
                  setAvatar(avatarValue);
                }
              }
            }
          } catch (fbError) {
            console.error('Error resolving handle -> uid or fetching bio/avatar:', fbError);
          }

          try {
            const handleRef = firebase.database().ref(`/handles/${handle}`);
            const handleSnap = await handleRef.once('value');
            if (handleSnap.exists()) {
              const uid = handleSnap.val();
              if (uid) {
                firebase.database().ref(`/stats/${uid}/profileViews`).transaction((current) => {
                  return (current || 0) + 1;
                });
              }
            }
          } catch (viewError) {
            console.error('Error tracking profile view:', viewError);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    }

    checkHandleAndLoadData();
  }, [handle]);

  // Post dynamic height to parent when inside an iframe
  useEffect(() => {
    try {
      if (window.self === window.top) return;
    } catch {}
    const postSize = () => {
      const height = document.body.scrollHeight;
      try {
        window.parent.postMessage({ type: 'addrs-embed:height', height }, '*');
      } catch {}
    };
    postSize();
    const ro = new ResizeObserver(() => postSize());
    ro.observe(document.body);
    const i = setInterval(postSize, 1000);
    return () => {
      try { ro.disconnect(); } catch {}
      clearInterval(i);
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading profile...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!handleExists) {
    return (
      <Box sx={{ background: 'transparent', p: 2 }}>
        <Container maxWidth="sm">
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Handle Not Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              @{handle} doesn't exist yet
            </Typography>
            <Button
              href="/"
              variant="contained"
              sx={{
                background: gradients.primary,
                '&:hover': { background: gradients.primary, opacity: 0.9 },
              }}
            >
              Create Your Profile
            </Button>
          </GlassCard>
        </Container>
      </Box>
    );
  }

  const addressArray = Object.keys(addresses).map((coinKey) => {
    const coinData = coins[coinKey] || {};
    return {
      coin: coinData.name || coinKey,
      symbol: coinKey,
      address: addresses[coinKey],
      gradient: coinGradients[coinKey] || gradients.primary,
      icon: coinData.symbol || coinKey.charAt(0),
    };
  });

  return (
    <Box sx={{ background: '#ffffff00', minHeight: '100vh', display: 'flex', alignItems: 'center', py: 2 }}>
      <Container maxWidth="sm" style={{textAlign: 'center'}}>
        <Box sx={{ background: '#fff', borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <Box sx={{
            background: gradients.mesh,
            p: 2.5,
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Avatar
                src={avatar || undefined}
                sx={{
                  width: 80,
                  height: 80,
                  border: '3px solid white',
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.12)',
                  fontSize: '2rem',
                  fontWeight: 800,
                  background: gradients.secondary,
                  margin: '0 auto',
                  mb: 1.5,
                }}
              >
                {!avatar && handle?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', mb: 0.75 }}>
                @{handle}
              </Typography>
              {hasEthAddress(addresses) && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.4,
                    borderRadius: 1.5,
                    background: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    mb: 0.75,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: '#fff' }}>
                    {handleToEnsStyle(handle)}
                  </Typography>
                  <Box sx={{
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 0.75,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: '#fff',
                    background: 'rgba(255, 255, 255, 0.25)',
                    letterSpacing: '0.5px'
                  }}>ENS</Box>
                </Box>
              )}
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.9), fontSize: '0.875rem' }}>
                {bio}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                  Accepts Payment In
                </Typography>
                {addressArray.length > 0 ? (
                  <Stack spacing={1.25}>
                    {addressArray.map((coin, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        fullWidth
                        href={`/${handle}/${coin.symbol.toLowerCase()}`}
                        sx={{
                          py: 1.25,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          textTransform: 'none',
                          borderWidth: 0,
                          justifyContent: 'flex-start',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: coin.gradient,
                            opacity: 0.15,
                            zIndex: 0,
                          },
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            '&::before': {
                              opacity: 0.25,
                            }
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', position: 'relative', zIndex: 1 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.75,
                              background: coin.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.1rem',
                              fontWeight: 800,
                              color: '#fff',
                            }}
                          >
                            {coin.icon}
                          </Box>
                          <Box sx={{ textAlign: 'left', flex: 1 }}>
                            <Typography variant="body2" fontWeight={700} sx={{
background: coin.gradient, // e.g. 'linear-gradient(...)'
WebkitBackgroundClip: 'text',
WebkitTextFillColor: 'transparent',
backgroundClip: 'text',
color: 'transparent',
}}>
                              {coin.coin}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {coin.symbol}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                    No addresses set up yet
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
